/**
 * Optional list library served by the web server from a mounted folder.
 *
 * The folder holds the word lists in the paste format plus a catalogue:
 *
 *   voci.json     {"FR Unit 01": "FR-Unit01.txt", "EN Unit 01": "EN-Unit01.txt"}
 *   FR-Unit01.txt DE;FR ...
 *
 * The catalogue is optional: if it is missing the import screen simply offers
 * no library, so the app still works without the mount.
 */

/** One entry of the catalogue: a display name and the file that holds the list. */
export type LibraryEntry = {
  name: string;
  file: string;
};

/** Path the web server publishes the mounted folder under. */
export const LIBRARY_BASE = '/library/';

export const CATALOG_FILE = 'voci.json';

/**
 * File names are put into a URL, so anything that could escape the library
 * folder or address another host is rejected.
 */
export function isSafeFileName(file: string): boolean {
  if (file === '' || file.length > 200) return false;
  if (file.includes('/') || file.includes('\\')) return false;
  if (file.includes('..') || file.includes(':')) return false;
  return !/[\u0000-\u001f]/.test(file);
}

/**
 * Reads the catalogue. Accepted are the documented object form
 * `{"FR Unit 01": "FR-Unit01.txt"}` and an array of `{name, file}` objects,
 * because both are easy to write by hand. Invalid entries are skipped instead
 * of failing the whole catalogue.
 */
export function parseCatalog(raw: unknown): LibraryEntry[] {
  const pairs: Array<[unknown, unknown]> = [];

  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        pairs.push([record.name, record.file]);
      }
    }
  } else if (raw && typeof raw === 'object') {
    for (const [name, file] of Object.entries(raw as Record<string, unknown>)) {
      pairs.push([name, file]);
    }
  }

  const entries: LibraryEntry[] = [];
  const seen = new Set<string>();

  for (const [rawName, rawFile] of pairs) {
    if (typeof rawFile !== 'string') continue;
    const file = rawFile.trim();
    if (!isSafeFileName(file)) continue;

    const name = typeof rawName === 'string' && rawName.trim() !== '' ? rawName.trim() : file;
    if (seen.has(name)) continue;
    seen.add(name);

    entries.push({ name, file });
  }

  entries.sort((a, b) => a.name.localeCompare(b.name, 'de-CH'));
  return entries;
}

/** URL of one list file inside the library. */
export function libraryUrl(file: string): string {
  return LIBRARY_BASE + encodeURIComponent(file);
}

/** Loads the catalogue. Returns an empty list when no library is mounted. */
export async function loadCatalog(): Promise<LibraryEntry[]> {
  try {
    const response = await fetch(LIBRARY_BASE + CATALOG_FILE, { cache: 'no-store' });
    if (!response.ok) return [];
    return parseCatalog(await response.json());
  } catch {
    return [];
  }
}

/** Loads one word list. Throws so the screen can show why it failed. */
export async function loadList(file: string): Promise<string> {
  if (!isSafeFileName(file)) throw new Error('Ungültiger Dateiname');

  const response = await fetch(libraryUrl(file), { cache: 'no-store' });
  if (!response.ok) throw new Error(`Datei nicht gefunden (${response.status})`);

  return await response.text();
}
